import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { parseWeeklyScheduleFile } from '../lib/parseWeeklySchedule';
import HelpGuide from '../components/HelpGuide';

const RENTAL_BUILDINGS = [
  { id: 'M',  name: '35 Glenco Drive (Rented Town Hall Space)',     short: 'Glenco Dr',          address: '35 Glenco Drive, Paradise NL' },
  { id: 'N',  name: '1655 Topsail Road (Rented Town Hall Space)',   short: 'Topsail Rd',         address: '1655 Topsail Road, Paradise NL' },
  { id: 'D',  name: 'Wastewater Treatment Plant',                   short: 'Wastewater',         address: '619 St. Thomas Line, Paradise NL' },
  { id: 'F',  name: 'RPYCC',                                        short: 'RPYCC',              address: '1 Sarah Davis Way, Paradise NL' },
  { id: 'G',  name: 'Paradise Park Change Rooms',                   short: 'Park Change Rooms',  address: 'Sarah Davis Way, Paradise NL' },
  { id: 'O',  name: 'Paradise Park (Outside Washrooms)',            short: 'Park Outside WC',    address: 'Sarah Davis Way, Paradise NL' },
  { id: 'E',  name: 'Paradise Double Ice Complex',                  short: 'PDIC',               address: '1 Sarah Davis Way, Paradise NL' },
  { id: 'J',  name: 'Milton Clubhouse',                             short: 'Milton Clubhouse',   address: 'Milton Road, Paradise NL' },
  { id: 'H',  name: 'Dianne Whalen Memorial Soccer Complex',        short: 'Soccer Complex',     address: 'McNamara Drive, Paradise NL' },
  { id: 'K',  name: 'Peter Barry Duff Building',                    short: 'PBD Building',       address: '272 Topsail Pond Road, Paradise NL' },
  { id: 'P',  name: 'Peter Barry Duff Washrooms',                   short: 'PBD Washrooms',      address: '272 Topsail Pond Road, Paradise NL' },
  { id: 'I',  name: 'St. Thomas Line Community',                    short: 'St. Thomas Line',    address: '2 Neary Road, Paradise NL' },
  { id: 'Q',  name: 'Outdoor Washroom – Dianne Whalen Memorial Soccer Complex', short: 'Soccer Outside WC', address: 'McNamara Drive, Paradise NL' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().split('T')[0];
}

function getUpcomingMondays(count = 8) {
  const mondays = [];
  let current = new Date(getMonday(new Date()));
  for (let i = 0; i < count; i++) {
    mondays.push(current.toISOString().split('T')[0]);
    current = new Date(current);
    current.setDate(current.getDate() + 7);
  }
  return mondays;
}

function formatWeekLabel(mondayStr) {
  const mon = new Date(mondayStr + 'T12:00:00');
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${mon.toLocaleDateString('en-CA', opts)} – ${sun.toLocaleDateString('en-CA', { ...opts, year: 'numeric' })}`;
}

function formatWeekShort(mondayStr) {
  const mon = new Date(mondayStr + 'T12:00:00');
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  return `Week of ${mon.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} – ${sun.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function formatRelativeTime(isoStr) {
  const mins = Math.floor((Date.now() - new Date(isoStr).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatDateShort(d) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function generateTimeOptions() {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${m === 0 ? '00' : '30'} ${ampm}`;
      const value = `${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`;
      opts.push({ label, value });
    }
  }
  return opts;
}

const TIME_OPTIONS = generateTimeOptions();

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (h === 0 && m === 0) return '12:00 AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

function getDayLabel(weekStart, dayName, short = false) {
  const idx = DAYS.indexOf(dayName);
  if (idx < 0) return dayName;
  const d = new Date(weekStart + 'T12:00:00');
  d.setDate(d.getDate() + idx);
  if (short) {
    const dayAbbr = d.toLocaleDateString('en-CA', { weekday: 'short' });
    const dateStr = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
    return `${dayAbbr} ${dateStr}`;
  }
  return d.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
}

function emptyBuildingData() {
  return { windows: [], min_staff: 1, restrictions: '', notes: '' };
}

// Stable string for one building's schedule, used to tell which specific
// buildings were edited after the week was submitted.
function buildingSignature(d) {
  if (!d) return '';
  return JSON.stringify({
    windows: d.windows || [],
    min_staff: d.min_staff || 1,
    restrictions: d.restrictions || '',
    notes: d.notes || '',
  });
}

function formatSubmittedStamp(iso) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return null;
  return dt.toLocaleString('en-CA', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function defaultEventForm() {
  return {
    name: '',
    building_id: '',
    location: '',
    start_date: '',
    end_date: '',
    notes: '',
    details: {
      description: '',
      time_start: '',
      time_end: '',
      setup_requirements: '',
      cleanup_requirements: '',
      estimated_attendance: '',
    },
  };
}

export default function TownPortalApp({ profile, onLogout }) {
  const mondays = getUpcomingMondays(8);
  const [weekStart, setWeekStart] = useState(mondays[0]);
  const [selectedBuildings, setSelectedBuildings] = useState(new Set());
  const [buildingData, setBuildingData] = useState({});
  const [view, setView] = useState('form'); // 'form' | 'events' | 'history'
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedWeek, setSavedWeek] = useState(null);
  // Whether the form has been edited since the last submit for this week.
  // Drives whether the footer offers "Resubmit" (there are real changes to
  // send) or just "View submitted schedule" — previously it always said
  // "Review & Update Week", which implied pending changes even when there
  // were none.
  const [changedSinceSubmit, setChangedSinceSubmit] = useState(false);
  // When this week was last submitted, and what was submitted, so the footer can
  // say "submitted X, but you have edits since" instead of hiding one fact
  // behind the other — and so each building card can show whether IT is the one
  // with unsent edits.
  const [submittedAt, setSubmittedAt] = useState(null);
  const [submittedSnapshot, setSubmittedSnapshot] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [copyMsg, setCopyMsg] = useState('');
  const [notifyWarn, setNotifyWarn] = useState(null); // saved, but email may not have sent
  const [loadingWeek, setLoadingWeek] = useState(false);

  // Special events state
  const [specialEvents, setSpecialEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(defaultEventForm());
  const [savingEvent, setSavingEvent] = useState(false);

  // Add-slot panel state (keyed by buildingId)
  const [collapsed, setCollapsed] = useState(new Set());
  const totalSlots = Array.from(selectedBuildings).reduce(
    (n, id) => n + ((buildingData[id]?.windows || []).length || 0), 0
  );

  // Which buildings differ from what was submitted — either edited, or added
  // to the week after the fact. Only meaningful once this week is submitted.
  const isSubmittedWeek = savedWeek === weekStart;
  const editedBuildings = new Set(
    isSubmittedWeek
      ? Array.from(selectedBuildings).filter(
          id => buildingSignature(buildingData[id]) !== (submittedSnapshot[id] || '')
        )
      : []
  );
  // A building that was submitted but has since been deselected is also an
  // unsent change, even though it no longer has a card on screen.
  const removedBuildingIds = isSubmittedWeek
    ? Object.keys(submittedSnapshot).filter(id => !selectedBuildings.has(id))
    : [];
  // For a submitted week, trust the actual comparison against what was sent
  // rather than the blanket "something was touched" flag — otherwise editing a
  // field and putting it back still nags you to resubmit.
  const hasUnsentChanges = isSubmittedWeek
    ? (editedBuildings.size > 0 || removedBuildingIds.length > 0)
    : changedSinceSubmit;
  function toggleCollapse(buildingId) {
    setCollapsed(prev => {
      const n = new Set(prev);
      n.has(buildingId) ? n.delete(buildingId) : n.add(buildingId);
      return n;
    });
  }
  const [weekEvents, setWeekEvents] = useState([]);
  const [loadingReview, setLoadingReview] = useState(false);

  // Copy-from-previous-week state
  const [showCopyPanel, setShowCopyPanel] = useState(false);
  const [prevWeekRecords, setPrevWeekRecords] = useState([]);
  const [copySelections, setCopySelections] = useState(new Set());
  const [loadingPrevWeek, setLoadingPrevWeek] = useState(false);

  // Draft & recovery state
  const [pendingDraft, setPendingDraft] = useState(null);
  const [recentlyDeleted, setRecentlyDeleted] = useState([]);
  const skipDraftSave = useRef(false); // true while loadWeekData is setting state

  // Import-from-document state
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { count, weekStart, warnings, unmatched } | null

  // localStorage key helpers
  const draftKey  = useCallback((week) => `tp_draft_${profile?.id}_${week}`, [profile?.id]);
  const deletedKey = useCallback(() => `tp_deleted_${profile?.id}`, [profile?.id]);

  // On mount: load draft for current week + recently deleted items
  useEffect(() => {
    const raw = localStorage.getItem(draftKey(mondays[0]));
    if (raw) { try { setPendingDraft(JSON.parse(raw)); } catch {} }

    const del = localStorage.getItem(deletedKey());
    if (del) {
      try {
        const items = JSON.parse(del);
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        setRecentlyDeleted(items.filter(i => new Date(i.deletedAt).getTime() > cutoff));
      } catch {}
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load this week's submitted data on mount so a returning session (e.g. after
  // logout/login) shows the previously submitted schedule pre-filled and editable.
  useEffect(() => {
    loadWeekData(weekStart);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft whenever building selection or data changes (skip DB-load triggers)
  useEffect(() => {
    if (skipDraftSave.current) return;
    if (selectedBuildings.size === 0) return;
    // Past these guards the change came from the user, not from loading the
    // week's saved data — which is exactly what "edited since submit" means.
    setChangedSinceSubmit(true);
    localStorage.setItem(draftKey(weekStart), JSON.stringify({
      selectedBuildings: Array.from(selectedBuildings),
      buildingData,
      savedAt: new Date().toISOString(),
    }));
  }, [selectedBuildings, buildingData]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWeekData = useCallback(async (week) => {
    skipDraftSave.current = true;
    setLoadingWeek(true);
    const { data } = await supabase
      .from('rental_schedules')
      .select('*')
      .eq('week_start', week);

    if (data && data.length > 0) {
      const ids = new Set(data.map(r => r.building_id));
      const bData = {};
      data.forEach(r => {
        bData[r.building_id] = {
          windows: r.windows || [],
          min_staff: r.min_staff || 1,
          restrictions: r.restrictions || '',
          notes: r.notes || '',
        };
      });
      setSelectedBuildings(ids);
      setBuildingData(bData);
      setSavedWeek(week); // a prior submission exists for this week → show "submitted" state
      // What's in the DB right now IS the submitted version — anything the user
      // changes from here counts as an unsent edit.
      const snap = {};
      Object.keys(bData).forEach(id => { snap[id] = buildingSignature(bData[id]); });
      setSubmittedSnapshot(snap);
      const stamps = data
        .map(r => r.updated_at || r.created_at)
        .filter(Boolean)
        .sort();
      setSubmittedAt(stamps.length > 0 ? stamps[stamps.length - 1] : null);
    } else {
      setSelectedBuildings(new Set());
      setBuildingData({});
      setSubmittedSnapshot({});
      setSubmittedAt(null);
    }
    setChangedSinceSubmit(false); // freshly loaded from the DB = nothing unsent
    setLoadingWeek(false);
    // Clear flag after React has flushed the state updates and run effects
    setTimeout(() => { skipDraftSave.current = false; }, 0);
  }, []);

  function handleWeekChange(week) {
    // Save current week's unsaved work before switching
    if (selectedBuildings.size > 0) {
      localStorage.setItem(draftKey(weekStart), JSON.stringify({
        selectedBuildings: Array.from(selectedBuildings),
        buildingData,
        savedAt: new Date().toISOString(),
      }));
    }
    setWeekStart(week);
    loadWeekData(week);
    setShowCopyPanel(false);
    // Check if new week has a saved draft
    const raw = localStorage.getItem(draftKey(week));
    if (raw) { try { setPendingDraft(JSON.parse(raw)); } catch { setPendingDraft(null); } }
    else { setPendingDraft(null); }
  }

  // Parses an uploaded copy of the weekly facility cleaning document and
  // pre-fills the same form state manual entry would produce — nothing is
  // saved to the DB until the Town reviews it below and hits Submit, same
  // as any other edit.
  async function handleImportFile(e) {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-choosing the same file if they fix something and re-upload
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await parseWeeklyScheduleFile(file);
      if (result.selectedBuildings.size === 0) {
        alert("Couldn't find any recognizable building schedules in that file. Nothing was imported — please check the file or enter the schedule manually below.");
        setImporting(false);
        return;
      }
      // Save whatever's on screen now as a draft first, same courtesy as
      // switching weeks manually, in case this overwrites unsaved work.
      if (selectedBuildings.size > 0) {
        localStorage.setItem(draftKey(weekStart), JSON.stringify({
          selectedBuildings: Array.from(selectedBuildings), buildingData, savedAt: new Date().toISOString(),
        }));
      }
      if (result.weekStart) setWeekStart(result.weekStart);
      setSelectedBuildings(result.selectedBuildings);
      setBuildingData(result.buildingData);
      setSavedWeek(null); // imported, not yet submitted
      setPendingDraft(null);
      setShowCopyPanel(false);
      setImportResult({
        count: result.selectedBuildings.size,
        weekStart: result.weekStart,
        warnings: result.warnings,
        unmatched: result.unmatched,
      });
    } catch (err) {
      alert('Could not read that file: ' + (err?.message || err));
    }
    setImporting(false);
  }

  function restoreDraft() {
    if (!pendingDraft) return;
    skipDraftSave.current = true;
    setSelectedBuildings(new Set(pendingDraft.selectedBuildings));
    setBuildingData(pendingDraft.buildingData);
    setPendingDraft(null);
    setTimeout(() => { skipDraftSave.current = false; }, 0);
  }

  function discardDraft() {
    localStorage.removeItem(draftKey(weekStart));
    setPendingDraft(null);
  }

  const loadSpecialEvents = useCallback(async () => {
    setLoadingEvents(true);
    // Show events from 30 days ago onwards so recent test entries are visible
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const cutoff = thirtyDaysAgo.toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('scheduled_events')
      .select('*')
      .eq('event_type', 'special_event')
      .gte('end_date', cutoff)
      .order('start_date', { ascending: true });

    if (error) console.error('loadSpecialEvents error:', error);
    if (data) setSpecialEvents(data);
    setLoadingEvents(false);
  }, []);

  useEffect(() => { if (view === 'events') loadSpecialEvents(); }, [view, loadSpecialEvents]);

  async function loadHistory() {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 84); // 12 weeks
    const cutoff = threeMonthsAgo.toISOString().split('T')[0];
    const { data } = await supabase
      .from('rental_schedules')
      .select('*')
      .gte('week_start', cutoff)
      .order('week_start', { ascending: false });
    if (data) setHistory(data);
  }

  useEffect(() => { if (view === 'history') loadHistory(); }, [view]);

  // ── Rental scheduling ────────────────────────────────────────────

  function toggleBuilding(id) {
    setSelectedBuildings(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!buildingData[id]) setBuildingData(d => ({ ...d, [id]: emptyBuildingData() }));
      }
      return next;
    });
  }

  function updateBuildingField(buildingId, field, value) {
    setBuildingData(prev => ({
      ...prev,
      [buildingId]: { ...(prev[buildingId] || emptyBuildingData()), [field]: value },
    }));
  }

  // Day-by-day grid helpers: one slot per date (keyed by day).
  function daySlotIndex(data, day) {
    return data.windows.findIndex(w => w.day === day);
  }
  function setDaySlot(buildingId, day, patch) {
    const data = buildingData[buildingId] || emptyBuildingData();
    const idx = daySlotIndex(data, day);
    if (idx >= 0) {
      const newWindows = data.windows.map((w, i) => i === idx ? { ...w, ...patch } : w);
      updateBuildingField(buildingId, 'windows', newWindows);
    } else {
      updateBuildingField(buildingId, 'windows', [...data.windows, { day, ...patch }]);
    }
  }
  function clearDaySlot(buildingId, day) {
    const data = buildingData[buildingId] || emptyBuildingData();
    updateBuildingField(buildingId, 'windows', data.windows.filter(w => w.day !== day));
  }
  function getDaySlot(buildingId, day) {
    const data = buildingData[buildingId] || emptyBuildingData();
    return data.windows.find(w => w.day === day) || null;
  }

  function removeWindow(buildingId, idx) {
    const data = buildingData[buildingId] || emptyBuildingData();
    updateBuildingField(buildingId, 'windows', data.windows.filter((_, i) => i !== idx));
  }

  async function openReview() {
    setView('review');
    setLoadingReview(true);
    const weekEnd = new Date(weekStart + 'T12:00:00');
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekEndStr = weekEnd.toISOString().split('T')[0];
    const { data } = await supabase
      .from('scheduled_events')
      .select('*')
      .eq('event_type', 'special_event')
      .lte('start_date', weekEndStr)
      .gte('end_date', weekStart)
      .order('start_date', { ascending: true });
    setWeekEvents(data || []);
    setLoadingReview(false);
  }

  async function handleSubmit() {
    if (selectedBuildings.size === 0) return;
    setSubmitError(null);
    setNotifyWarn(null);
    setSaving(true);

    // Capture prior DB state for this week so we only email NEW/CHANGED buildings
    // (a resubmit after adding building N shouldn't re-notify building M).
    const { data: priorRows } = await supabase
      .from('rental_schedules')
      .select('building_id, windows, min_staff')
      .eq('week_start', weekStart);
    const prior = {};
    (priorRows || []).forEach(r => {
      prior[r.building_id] = {
        windows: JSON.stringify(r.windows || []),
        min_staff: r.min_staff || 1,
      };
    });

    const rows = Array.from(selectedBuildings).map(bId => ({
      building_id: bId,
      week_start: weekStart,
      windows: (buildingData[bId]?.windows || []),
      min_staff: buildingData[bId]?.min_staff || 1,
      restrictions: buildingData[bId]?.restrictions || null,
      notes: buildingData[bId]?.notes || null,
      submitted_by: profile?.id,
      updated_at: new Date().toISOString(),
    }));

    const { error } = await supabase
      .from('rental_schedules')
      .upsert(rows, { onConflict: 'building_id,week_start' });

    const deselected = RENTAL_BUILDINGS.map(b => b.id).filter(id => !selectedBuildings.has(id));
    if (deselected.length > 0) {
      await supabase.from('rental_schedules').delete().eq('week_start', weekStart).in('building_id', deselected);
    }

    setSaving(false);
    if (error) {
      setSubmitError(error.message || 'Submit failed. Check your connection and try again.');
      return;
    }
    // Supabase RLS can silently drop an INSERT (returns no error, 0 rows written).
    // Detect that and surface it instead of a false "submitted" success.
    const { data: verify, error: verifyErr } = await supabase
      .from('rental_schedules')
      .select('building_id')
      .eq('week_start', weekStart)
      .in('building_id', Array.from(selectedBuildings));
    if (verifyErr) {
      setSubmitError(verifyErr.message || 'Could not confirm submission.');
      return;
    }
    if (!verify || verify.length === 0) {
      console.error('Submission verification returned no rows — likely blocked by RLS on rental_schedules.');
      setSubmitError('The schedule was NOT saved — your account may not have permission to submit. Please contact TC Janitorial.');
      return;
    }
    setSubmitError(null);
    localStorage.removeItem(draftKey(weekStart));
    setSavedWeek(weekStart);
    setChangedSinceSubmit(false);
    setSubmittedAt(new Date().toISOString());

    // Only notify for buildings that are NEW or whose schedule CHANGED since last submit.
    const changedRows = rows.filter(r => {
      const p = prior[r.building_id];
      if (!p) return true; // new building
      const same = p.windows === JSON.stringify(r.windows || []) && p.min_staff === (r.min_staff || 1);
      return !same; // changed
    });
    if (changedRows.length > 0) {
      notifyScheduleSubmitted(changedRows, rows);
    }

    loadWeekData(weekStart); // refresh from DB so the Review summary reflects what was persisted
  }

  // Notify Fabian, Cynthia, and Jason (Town manager) that a schedule was submitted.
  // Fire-and-forget: never blocks or fails the actual submission if email is down.
  // `rows` = buildings that changed (drives whether we send); `allRows` = full week's
  // buildings (shown in the email so Fabian sees the complete picture).
  async function notifyScheduleSubmitted(rows, allRows) {
    try {
      const weekRows = allRows && allRows.length ? allRows : rows;
      const buildingNames = weekRows
        .map(r => RENTAL_BUILDINGS.find(b => b.id === r.building_id)?.short || r.building_id)
        .join(', ');
      const submittedByName = profile?.full_name || 'Town of Paradise';
      const weekLabel = formatWeekLabel(weekStart);

      const subject = `Cleaning schedule update — ${weekLabel}`;
      const body =
        `${submittedByName} submitted/updated the weekly cleaning schedule for ${weekLabel}.\n\n` +
        `Buildings in this week's schedule: ${buildingNames}\n` +
        `Total buildings this week: ${weekRows.length}\n` +
        (rows.length < weekRows.length
          ? `Buildings changed in this update: ${rows.map(r => RENTAL_BUILDINGS.find(b => b.id === r.building_id)?.short || r.building_id).join(', ')}\n`
          : '') +
        `\nReview and assign staff in TraverseClean: https://traverseclean.com`;

      // Fabian + Cynthia: operational team, need to assign staff
      await supabase.functions.invoke('send-notification', {
        body: {
          to: ['fabian@fabiantraverse.com', 'cynthia@tcjanitorial.ca'],
          subject,
          body,
        },
      });

      // Jason: Town manager, notified regardless of who entered the data
      if (profile?.email !== 'jlawrence@paradise.ca') {
        await supabase.functions.invoke('send-notification', {
          body: {
            to: 'jlawrence@paradise.ca',
            subject,
            body: `${body}\n\n(You're notified as the Town of Paradise contact on record, regardless of who submitted this.)`,
          },
        });
      }
    } catch (e) {
      // Never let a notification failure affect the user's save confirmation,
      // but DO surface it so a broken email setup isn't silent.
      console.error('notifyScheduleSubmitted failed:', e);
      setNotifyWarn('Schedule saved, but the email notification could not be sent. ' +
        'Ask the app admin to check the send-notification function / RESEND_API_KEY.');
    }
  }

  async function copyFromLastWeek(buildingId) {
    setCopyMsg('');
    const prevMonday = new Date(weekStart + 'T12:00:00');
    prevMonday.setDate(prevMonday.getDate() - 7);
    const { data } = await supabase
      .from('rental_schedules')
      .select('*')
      .eq('building_id', buildingId)
      .eq('week_start', prevMonday.toISOString().split('T')[0])
      .single();

    if (data) {
      setBuildingData(prev => ({
        ...prev,
        [buildingId]: {
          windows: data.windows || [],
          min_staff: data.min_staff || 1,
          restrictions: data.restrictions || '',
          notes: data.notes || '',
        },
      }));
      setCopyMsg('Copied last week’s schedule for ' + (RENTAL_BUILDINGS.find(b => b.id === buildingId)?.short || buildingId));
    } else {
      setCopyMsg('No schedule found for last week to copy');
    }
    setTimeout(() => setCopyMsg(''), 4000);
  }

  // ── Copy from previous week ──────────────────────────────────────

  function getPrevWeekStr() {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  }

  async function openCopyPanel() {
    setLoadingPrevWeek(true);
    setShowCopyPanel(true);
    const prevStr = getPrevWeekStr();
    const { data } = await supabase
      .from('rental_schedules')
      .select('*')
      .eq('week_start', prevStr);
    const records = data || [];
    setPrevWeekRecords(records);
    // Pre-select all buildings not already in current week
    setCopySelections(new Set(records.map(r => r.building_id).filter(id => !selectedBuildings.has(id))));
    setLoadingPrevWeek(false);
  }

  function toggleCopySelection(id) {
    setCopySelections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function applyCopy() {
    const newSelected = new Set(selectedBuildings);
    const newBuildingData = { ...buildingData };

    prevWeekRecords.forEach(r => {
      if (!copySelections.has(r.building_id)) return;
      newSelected.add(r.building_id);
      newBuildingData[r.building_id] = {
        windows: r.windows || [],
        min_staff: r.min_staff || 1,
        restrictions: r.restrictions || '',
        notes: r.notes || '',
      };
    });

    setSelectedBuildings(newSelected);
    setBuildingData(newBuildingData);
    setShowCopyPanel(false);
  }

  // ── Special events ───────────────────────────────────────────────

  function openAddEvent() {
    setEventForm(defaultEventForm());
    setEditingEvent(null);
    setShowEventForm(true);
  }

  function openEditEvent(event) {
    setEventForm({
      name: event.name || '',
      building_id: event.building_id || '',
      location: event.location || '',
      start_date: event.start_date || '',
      end_date: event.end_date && event.end_date !== event.start_date ? event.end_date : '',
      notes: event.notes || '',
      details: {
        description: event.details?.description || '',
        time_start: event.details?.time_start || '',
        time_end: event.details?.time_end || '',
        setup_requirements: event.details?.setup_requirements || '',
        cleanup_requirements: event.details?.cleanup_requirements || '',
        estimated_attendance: event.details?.estimated_attendance || '',
      },
    });
    setEditingEvent(event);
    setShowEventForm(true);
  }

  function updateDetail(field, value) {
    setEventForm(prev => ({ ...prev, details: { ...prev.details, [field]: value } }));
  }

  async function handleSaveEvent() {
    if (!eventForm.name || !eventForm.start_date) return;
    setSavingEvent(true);

    const b = RENTAL_BUILDINGS.find(x => x.id === eventForm.building_id);
    const location = eventForm.location || b?.name || '';
    const endDate = eventForm.end_date || eventForm.start_date;

    const payload = {
      event_type: 'special_event',
      name: eventForm.name,
      building_id: eventForm.building_id || null,
      location,
      start_date: eventForm.start_date,
      end_date: endDate,
      is_multi_day: endDate !== eventForm.start_date,
      hourly_billing: true,
      notes: eventForm.notes || null,
      details: eventForm.details,
      submitted_by: profile?.id,
    };

    if (editingEvent) {
      await supabase.from('scheduled_events').update(payload).eq('id', editingEvent.id);
    } else {
      await supabase.from('scheduled_events').insert([payload]);
    }

    await loadSpecialEvents();
    setShowEventForm(false);
    setSavingEvent(false);
  }

  async function handleDeleteEvent(id) {
    if (!window.confirm('Remove this special event?')) return;
    await supabase.from('scheduled_events').delete().eq('id', id);
    await loadSpecialEvents();
  }

  function saveRecentlyDeleted(items) {
    localStorage.setItem(deletedKey(), JSON.stringify(items));
  }

  async function handleDeleteSchedule(record) {
    if (!window.confirm('Remove this entry? It can be recovered from Recently Deleted for 7 days.')) return;
    const updated = [
      { ...record, deletedAt: new Date().toISOString() },
      ...recentlyDeleted.filter(d => !(d.building_id === record.building_id && d.week_start === record.week_start)),
    ].slice(0, 50);
    setRecentlyDeleted(updated);
    saveRecentlyDeleted(updated);
    await supabase.from('rental_schedules').delete()
      .eq('building_id', record.building_id)
      .eq('week_start', record.week_start);
    await loadHistory();
  }

  async function handleRestoreDeleted(item) {
    const { deletedAt, ...record } = item;
    await supabase.from('rental_schedules')
      .upsert({ ...record, updated_at: new Date().toISOString() }, { onConflict: 'building_id,week_start' });
    const updated = recentlyDeleted.filter(d => !(d.building_id === item.building_id && d.week_start === item.week_start));
    setRecentlyDeleted(updated);
    saveRecentlyDeleted(updated);
    await loadHistory();
  }

  function handlePermanentDelete(item) {
    const updated = recentlyDeleted.filter(d => !(d.building_id === item.building_id && d.week_start === item.week_start));
    setRecentlyDeleted(updated);
    saveRecentlyDeleted(updated);
  }

  async function handleDeleteWeekSchedules(week) {
    if (!window.confirm(`Remove the whole cleaning schedule for ${formatWeekShort(week)}? This cannot be undone.`)) return;
    // Save all records for this week to recently deleted before removing
    const weekRecords = history.filter(r => r.week_start === week);
    if (weekRecords.length > 0) {
      const now = new Date().toISOString();
      const updated = [
        ...weekRecords.map(r => ({ ...r, deletedAt: now })),
        ...recentlyDeleted.filter(d => d.week_start !== week),
      ].slice(0, 50);
      setRecentlyDeleted(updated);
      saveRecentlyDeleted(updated);
    }
    await supabase.from('rental_schedules').delete().eq('week_start', week);
    await loadHistory();
  }

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div style={s.screen}>
      <style>{`
        input[type="time"]::-webkit-calendar-picker-indicator,
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1); opacity: 0.55; cursor: pointer;
        }
      `}</style>

      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.logo}>
            <div style={s.logoMark}>TP</div>
            <div>
              <div style={s.logoName}>Town of Paradise</div>
              <div style={s.logoSub}>Cleaning Schedule Portal</div>
            </div>
          </div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
            Signed in as {profile?.full_name || 'Jason Lawrence'}
          </div>
        </div>
        <button style={s.signOutBtn} onClick={onLogout}>Sign Out</button>
      </div>

      {/* Tabs */}
      <div style={s.tabRow}>
        <button style={view === 'form'    ? { ...s.tab, ...s.tabActive } : s.tab} onClick={() => { setView('form'); setShowEventForm(false); }}>Weekly Schedule</button>
        <button style={view === 'events'  ? { ...s.tab, ...s.tabActive } : s.tab} onClick={() => { setView('events'); setShowEventForm(false); }}>Special Events</button>
        <button style={view === 'history' ? { ...s.tab, ...s.tabActive } : s.tab} onClick={() => setView('history')}>History</button>
        <button style={view === 'help'    ? { ...s.tab, ...s.tabActive } : s.tab} onClick={() => setView('help')}>Help</button>
      </div>

      {view === 'help' && (
        <div style={s.content}>
          <HelpGuide guide="town" />
        </div>
      )}

      {/* ── Rental Schedule ── */}
      {view === 'form' && (
        <div style={s.content}>
          <div style={s.section}>
            <div style={s.sectionLabel}>SELECT WEEK</div>
            <select style={s.weekSelect} value={weekStart} onChange={e => handleWeekChange(e.target.value)}>
              {mondays.map(m => <option key={m} value={m}>{formatWeekLabel(m)}</option>)}
            </select>
            {loadingWeek && <div style={s.loadingNote}>Loading existing data...</div>}
            {!loadingWeek && !showCopyPanel && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={{ ...s.copyWeekBtn, width: 'auto', flex: 1, display: 'inline-block' }} onClick={openCopyPanel}>
                  ⎘ Copy from previous week
                </button>
                <button
                  style={{ ...s.copyWeekBtn, width: 'auto', flex: 1, display: 'inline-block', opacity: importing ? 0.6 : 1 }}
                  onClick={() => importFileRef.current?.click()}
                  disabled={importing}
                >
                  {importing ? 'Reading file…' : '📄 Upload this week\'s document'}
                </button>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleImportFile}
                />
              </div>
            )}
          </div>

          {/* ── Import result banner ── */}
          {importResult && (
            <div style={{
              margin: '0 12px 4px', borderRadius: 12, padding: '14px 16px',
              background: importResult.warnings.length > 0 || importResult.unmatched.length > 0 ? '#3f2f0d' : '#0c1f0f',
              border: `1px solid ${importResult.warnings.length > 0 || importResult.unmatched.length > 0 ? '#b9911c' : '#166534'}`,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: '#fff', fontSize: 14 }}>
                Imported {importResult.count} building{importResult.count !== 1 ? 's' : ''}
                {importResult.weekStart ? ` for ${formatWeekLabel(importResult.weekStart)}` : ''}
              </div>
              <div style={{ fontSize: 13, marginBottom: 6, color: '#cbd5e1', lineHeight: 1.5 }}>Nothing was submitted yet — review each building below, then hit Submit when it looks right.</div>
              {importResult.unmatched.length > 0 && (
                <div style={{ fontSize: 13, marginBottom: 4, color: '#fcd34d', lineHeight: 1.5 }}>
                  ⚠️ {importResult.unmatched.length} section{importResult.unmatched.length !== 1 ? 's' : ''} in the file didn't match a known building and were skipped — add {importResult.unmatched.length !== 1 ? 'those' : 'it'} manually: {importResult.unmatched.map(u => `"${u.rawTitle.split('\n')[0].slice(0, 40)}"`).join(', ')}
                </div>
              )}
              {importResult.warnings.length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#fcd34d', lineHeight: 1.6 }}>
                  {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              <button style={{ ...s.draftDiscardBtn, marginTop: 8, flex: 'none', padding: '6px 14px' }} onClick={() => setImportResult(null)}>Dismiss</button>
            </div>
          )}

          {/* ── Draft restore banner ── */}
          {pendingDraft && !loadingWeek && savedWeek !== weekStart && (
            <div style={s.draftBannerCard}>
              <div style={s.draftBannerText}>
                Unsaved draft found — last edited {formatRelativeTime(pendingDraft.savedAt)} with {pendingDraft.selectedBuildings.length} building{pendingDraft.selectedBuildings.length !== 1 ? 's' : ''}.
              </div>
              <div style={s.draftBannerActions}>
                <button style={s.draftRestoreBtn} onClick={restoreDraft}>Restore Draft</button>
                <button style={s.draftDiscardBtn} onClick={discardDraft}>Discard</button>
              </div>
            </div>
          )}

          {/* ── Copy Panel ── */}
          {showCopyPanel && (
            <div style={s.copyPanel}>
              <div style={s.copyPanelHeader}>
                <div>
                  <div style={s.copyPanelTitle}>Copy from {formatWeekLabel(getPrevWeekStr())}</div>
                  <div style={s.copyPanelSub}>Select buildings to copy. Already-existing buildings are pre-deselected.</div>
                </div>
                <button style={s.copyPanelClose} onClick={() => setShowCopyPanel(false)}>✕</button>
              </div>

              {loadingPrevWeek && <div style={s.loadingNote}>Loading previous week...</div>}

              {!loadingPrevWeek && prevWeekRecords.length === 0 && (
                <div style={s.copyEmpty}>No schedule was submitted for the previous week.</div>
              )}

              {!loadingPrevWeek && prevWeekRecords.map(r => {
                const b = RENTAL_BUILDINGS.find(x => x.id === r.building_id);
                const checked = copySelections.has(r.building_id);
                const alreadyExists = selectedBuildings.has(r.building_id);
                return (
                  <label key={r.building_id} style={checked ? { ...s.copyItem, ...s.copyItemChecked } : s.copyItem}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCopySelection(r.building_id)}
                      style={s.copyCheckbox}
                    />
                    <div style={s.copyItemBody}>
                      <div style={s.copyItemName}>
                        <span style={s.copyItemId}>{r.building_id}</span>
                        {b?.short || r.building_id}
                        {alreadyExists && <span style={s.copyOverwriteTag}>will overwrite</span>}
                      </div>
                      <div style={s.copyItemMeta}>
                        {(r.windows || []).length} time slot{r.windows?.length !== 1 ? 's' : ''} · min {r.min_staff} staff
                        {r.restrictions ? ` · ⚠️ restrictions` : ''}
                      </div>
                      {(r.windows || []).length > 0 && (
                        <div style={s.copyWindowList}>
                          {r.windows.slice(0, 3).map((w, i) => (
                            <span key={i} style={s.copyWindowChip}>
                              {w.day.slice(0, 3)} {formatTime(w.start)}–{formatTime(w.end)}
                            </span>
                          ))}
                          {r.windows.length > 3 && <span style={s.copyWindowChip}>+{r.windows.length - 3} more</span>}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}

              {!loadingPrevWeek && prevWeekRecords.length > 0 && (
                <div style={s.copyActions}>
                  <button
                    style={copySelections.size === 0 ? { ...s.copyApplyBtn, opacity: 0.4 } : s.copyApplyBtn}
                    disabled={copySelections.size === 0}
                    onClick={applyCopy}
                  >
                    Copy {copySelections.size} Building{copySelections.size !== 1 ? 's' : ''}
                  </button>
                  <button style={s.copyCancelBtn} onClick={() => setShowCopyPanel(false)}>Cancel</button>
                </div>
              )}
            </div>
          )}

          <div style={s.section}>
            <div style={s.sectionLabel}>BUILDINGS NEEDING CLEANING THIS WEEK</div>
            <div style={s.buildingChips}>
              {RENTAL_BUILDINGS.map(b => (
                <button
                  key={b.id}
                  style={selectedBuildings.has(b.id) ? { ...s.chip, ...s.chipActive } : s.chip}
                  onClick={() => toggleBuilding(b.id)}
                >
                  <span style={selectedBuildings.has(b.id) ? { ...s.chipId, ...s.chipIdActive } : s.chipId}>{b.id}</span>
                  <span style={selectedBuildings.has(b.id) ? { ...s.chipName, ...s.chipNameActive } : s.chipName}>{b.short}</span>
                </button>
              ))}
            </div>
            {selectedBuildings.size === 0 && (
              <div style={s.hintText}>Tap the buildings that need cleaning this week</div>
            )}
            {selectedBuildings.size > 0 && (
              <div style={s.hintText}>{selectedBuildings.size} building{selectedBuildings.size !== 1 ? 's' : ''} selected</div>
            )}
            {copyMsg && (
              <div style={s.copyMsg}>{copyMsg}</div>
            )}
          </div>

          {RENTAL_BUILDINGS.filter(b => selectedBuildings.has(b.id)).map(b => {
            const data = buildingData[b.id] || emptyBuildingData();
            return (
              <div
                key={b.id}
                style={editedBuildings.has(b.id)
                  ? { ...s.buildingCard, borderLeft: '3px solid #fcd34d', border: '1px solid #b9911c', borderLeftColor: '#fcd34d' }
                  : s.buildingCard}
              >
                <div style={s.buildingCardHeader}>
                  <button style={s.collapseToggle} onClick={() => toggleCollapse(b.id)}>
                    <div style={s.buildingCardId}>{b.id}</div>
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <div style={s.buildingCardName}>{b.name}</div>
                      {editedBuildings.has(b.id) && (
                        <div style={s.editedBadge}>
                          ● {submittedSnapshot[b.id] ? 'Edited since submit' : 'Added since submit'} — not sent yet
                        </div>
                      )}
                      {collapsed.has(b.id) && (
                        <div style={s.collapsedHint}>
                          {data.windows.length} slot{data.windows.length !== 1 ? 's' : ''} set · tap to expand
                        </div>
                      )}
                    </div>
                    <span style={s.collapseLabel}>{collapsed.has(b.id) ? 'Expand ▾' : 'Collapse ▴'}</span>
                  </button>
                  <div style={s.headerActions}>
                    <button style={s.copyBtn} onClick={() => copyFromLastWeek(b.id)}>Copy last week</button>
                  </div>
                </div>

                {!collapsed.has(b.id) && (
                <>
                <div style={s.fieldLabel}>SCHEDULE BY DAY</div>
                <div style={s.dayGrid}>
                  {DAYS.map(d => {
                    const slot = getDaySlot(b.id, d);
                    const type = slot ? (slot.type || 'time') : 'none';
                    // getDaySlot only ever surfaces the first window for a day —
                    // this control was never built to show or edit a second one,
                    // so a day with more than one cleaning (e.g. a rental-driven
                    // building needing two visits) silently looks like just one
                    // here. Flag it rather than hide it; Review already lists
                    // every slot correctly and is where it gets managed.
                    const dayWindowCount = data.windows.filter(w => w.day === d).length;
                    return (
                      <div key={d} style={s.dayRow}>
                        <span style={s.dayRowLabel}>{getDayLabel(weekStart, d, true)}</span>
                        <select
                          style={s.dayTypeSelect}
                          value={type}
                          onChange={e => {
                            const t = e.target.value;
                            if (t === 'none') { clearDaySlot(b.id, d); return; }
                            setDaySlot(b.id, d, {
                              type: t,
                              min_staff: slot?.min_staff || data.min_staff || 1,
                              ...(t === 'time' ? { start: slot?.start || '13:00', end: slot?.end || '14:00' } : {}),
                            });
                          }}
                        >
                          <option value="none">— none —</option>
                          <option value="time">Specific Time</option>
                          <option value="anytime">Anytime</option>
                          <option value="tc_schedule">TC to Schedule</option>
                        </select>
                        {type === 'time' && (
                          <>
                            <select style={s.dayTimeSelect} value={slot?.start || '13:00'} onChange={e => setDaySlot(b.id, d, { type, start: e.target.value })}>
                              {TIME_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                            <span style={s.timeSep}>→</span>
                            <select style={s.dayTimeSelect} value={slot?.end || '14:00'} onChange={e => setDaySlot(b.id, d, { type, end: e.target.value })}>
                              {TIME_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                          </>
                        )}
                        {type !== 'none' && (
                          <select
                            style={s.dayStaffSelect}
                            value={slot?.min_staff || 1}
                            onChange={e => setDaySlot(b.id, d, { type, min_staff: Number(e.target.value) })}
                          >
                            {[1, 2, 3].map(n => <option key={n} value={n}>{n}{n === 1 ? ' person' : ' people'}</option>)}
                          </select>
                        )}
                        {slot && (
                          <button style={s.removeBtn} onClick={() => clearDaySlot(b.id, d)} title="Clear day">✕</button>
                        )}
                        {dayWindowCount > 1 && (
                          <div style={s.multiSlotHint}>
                            ⓘ {dayWindowCount} cleanings this day — manage on the Review screen
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={s.fieldLabel}>RESTRICTIONS (optional)</div>
                <textarea
                  style={s.textarea}
                  rows={2}
                  placeholder="e.g. No cleaning during council meetings 3:30–10pm on Tuesdays"
                  value={data.restrictions}
                  onChange={e => updateBuildingField(b.id, 'restrictions', e.target.value)}
                />

                <div style={s.fieldLabel}>BUILDING INSTRUCTIONS (optional)</div>
                <textarea
                  style={s.textarea}
                  rows={2}
                  placeholder="e.g. Check with reception daily for schedule — or — Clean once daily after 9pm (set alarm)"
                  value={data.notes || ''}
                  onChange={e => updateBuildingField(b.id, 'notes', e.target.value)}
                />
              </>)}
              </div>
            );
          })}

          {selectedBuildings.size > 0 && (
            <div style={s.submitArea}>
              {/* One status line, not two. This used to show a green
                  "✓ Schedule submitted" banner and immediately below it a
                  second pill reading "Submitted — add more buildings or
                  resubmit", which looked like a button and repeated what the
                  banner already said. */}
              {isSubmittedWeek ? (
                <>
                  {/* The "already submitted" fact and the "you have edits" fact
                      are both shown. Previously they were an either/or in this
                      one slot, so the moment you edited anything the
                      confirmation that a version HAD been sent disappeared —
                      leaving no way to tell an edited submission apart from a
                      week that was never submitted at all. */}
                  <div style={s.savedBanner}>
                    ✓ Schedule submitted for {formatWeekLabel(weekStart)}
                    {formatSubmittedStamp(submittedAt) && ` · last sent ${formatSubmittedStamp(submittedAt)}`}
                  </div>
                  {hasUnsentChanges && (
                    <div style={s.unsentBanner}>
                      ● Unsent changes since then — resubmit to send them to TC Janitorial
                      {(editedBuildings.size > 0 || removedBuildingIds.length > 0) && (
                        <div style={s.unsentDetail}>
                          {editedBuildings.size > 0 && `Edited: ${Array.from(editedBuildings).join(', ')}`}
                          {editedBuildings.size > 0 && removedBuildingIds.length > 0 && ' · '}
                          {removedBuildingIds.length > 0 && `Removed: ${removedBuildingIds.join(', ')}`}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div style={s.draftBanner}>Draft — not yet sent to TC Janitorial</div>
              )}
              {savedWeek === weekStart ? (
                <>
                  <button
                    style={s.addBuildingBtn}
                    onClick={() => { setSelectedBuildings(new Set()); setBuildingData({}); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  >
                    + Add another building →
                  </button>
                  <button style={s.reviewBtn} onClick={openReview}>
                    {hasUnsentChanges ? 'Review & Resubmit →' : 'View submitted schedule →'}
                  </button>
                  <div style={s.submitNote}>
                    {hasUnsentChanges
                      ? 'Your changes are not sent yet. Review them, then resubmit — Fabian is notified each time you submit.'
                      : 'Everything here has been sent to Fabian. To change it, edit a building above or add another, then resubmit.'}
                  </div>
                </>
              ) : (
                <>
                  <button
                    style={totalSlots === 0 ? { ...s.reviewBtn, opacity: 0.45, cursor: 'not-allowed' } : s.reviewBtn}
                    disabled={totalSlots === 0}
                    onClick={() => totalSlots > 0 && openReview()}
                  >
                    Review &amp; Submit →
                  </button>
                  <div style={s.submitNote}>
                    {totalSlots === 0
                      ? 'Add at least one time slot (use the day grid on a building) before reviewing.'
                      : 'Review your complete schedule before sending to Fabian.'}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Review & Submit ── */}
      {view === 'review' && (
        <div style={s.content}>
          <div style={s.formPageHeader}>
            <button style={s.backBtn} onClick={() => setView('form')}>← Back to Edit</button>
            <div style={s.formPageTitle}>Review Schedule</div>
          </div>

          <div style={s.reviewWeekHeader}>
            <div style={s.reviewWeekTitle}>{formatWeekLabel(weekStart)}</div>
            <div style={s.reviewWeekSub}>
              {selectedBuildings.size} building{selectedBuildings.size !== 1 ? 's' : ''} · draft — not yet sent to TC Janitorial
            </div>
          </div>

          {RENTAL_BUILDINGS.filter(b => selectedBuildings.has(b.id) || (buildingData[b.id] && buildingData[b.id].windows.length > 0)).map(b => {
            const data = buildingData[b.id] || emptyBuildingData();
            const slotsByDay = {};
            data.windows.forEach((w, idx) => {
              if (!slotsByDay[w.day]) slotsByDay[w.day] = [];
              slotsByDay[w.day].push({ w, idx });
            });
            const isSelected = selectedBuildings.has(b.id);
            return (
              <div key={b.id} style={s.reviewCard}>
                <div style={s.reviewCardHeader}>
                  <div style={s.buildingCardId}>{b.id}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.buildingCardName}>{b.name}</div>
                    {b.address && <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>📍 {b.address}</div>}
                    {!isSelected && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>⚠️ Not selected — will NOT be submitted. Tap its chip on the form to include it.</div>}
                  </div>
                  <button style={s.reviewEditBtn} onClick={() => setView('form')}>Edit</button>
                </div>

                {data.windows.length === 0 ? (
                  <div style={s.reviewNoSlots}>No time slots added</div>
                ) : (
                  <div style={s.reviewSlotsList}>
                    {DAYS.filter(d => slotsByDay[d]).map(d => (
                      <div key={d} style={s.reviewDayRow}>
                        <span style={s.reviewDayLabel}>{getDayLabel(weekStart, d, true)}</span>
                        <div style={s.reviewSlotChips}>
                          {slotsByDay[d].map(({ w, idx }) => {
                            const t = w.type || 'time';
                            const typeLabel = t === 'time' ? 'Specific Time'
                              : t === 'anytime' ? 'Anytime' : 'TC to Schedule';
                            const timeLabel = t === 'time'
                              ? (w.end ? `${formatTime(w.start || '13:00')} – ${formatTime(w.end)}` : `after ${formatTime(w.start || '13:00')}`)
                              : '—';
                            const staff = w.min_staff || 1;
                            return (
                              <div key={idx} style={s.reviewSlotLine}>
                                <span style={s.reviewSlotDay}>{getDayLabel(weekStart, d, true)}</span>
                                <span style={s.reviewSlotType}>{typeLabel}</span>
                                <span style={s.reviewSlotTime}>{timeLabel}</span>
                                <span style={s.reviewSlotStaff}>{staff} {staff === 1 ? 'person' : 'people'}</span>
                                <button style={s.reviewSlotRemove} onClick={() => removeWindow(b.id, idx)} title="Remove">✕</button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={s.reviewMeta}>
                  Staff required per slot (see each above)
                  {data.restrictions && <span style={{ color: '#f59e0b' }}> · ⚠️ {data.restrictions}</span>}
                </div>
                {data.notes && (
                  <div style={s.reviewBuildingNote}>{data.notes}</div>
                )}
              </div>
            );
          })}

          {loadingReview && <div style={{ ...s.loadingNote, padding: '16px 0' }}>Loading special events...</div>}

          {!loadingReview && weekEvents.length > 0 && (
            <div style={s.reviewEventsSection}>
              <div style={{ ...s.sectionLabel, padding: '0 16px' }}>SPECIAL EVENTS THIS WEEK</div>
              {weekEvents.map(event => {
                const b = RENTAL_BUILDINGS.find(x => x.id === event.building_id);
                const det = event.details || {};
                return (
                  <div key={event.id} style={s.reviewEventRow}>
                    <div style={s.reviewEventName}>{event.name}</div>
                    <div style={s.reviewEventMeta}>
                      {b ? `${b.id} · ${b.name}` : event.location}
                      {' · '}{formatDate(event.start_date)}
                      {det.time_start && ` · ${formatTime(det.time_start)}${det.time_end ? `–${formatTime(det.time_end)}` : ''}`}
                    </div>
                    {det.setup_requirements && (
                      <div style={s.reviewEventDetail}>Setup: {det.setup_requirements}</div>
                    )}
                    {det.cleanup_requirements && (
                      <div style={s.reviewEventDetail}>Cleanup: {det.cleanup_requirements}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loadingReview && weekEvents.length === 0 && (
            <div style={s.reviewNoEvents}>No special events scheduled for this week.</div>
          )}

          <div style={s.submitArea}>
            {submitError && (
              <div style={s.errorBanner}>⚠️ {submitError}</div>
            )}
            {notifyWarn && !submitError && (
              <div style={s.warnBanner}>⚠️ {notifyWarn}</div>
            )}
            {savedWeek === weekStart && !submitError && (
              <div style={s.savedBanner}>✓ Submitted for {formatWeekLabel(weekStart)} — review the summary below. You can edit and resubmit anytime.</div>
            )}
            <button
              style={saving ? { ...s.submitBtn, opacity: 0.6 } : (totalSlots === 0 ? { ...s.submitBtn, opacity: 0.45, cursor: 'not-allowed' } : s.submitBtn)}
              disabled={saving || totalSlots === 0}
              onClick={handleSubmit}
            >
              {saving ? 'Submitting...' : (savedWeek === weekStart ? 'Update Week Schedule →' : 'Submit Week Schedule →')}
            </button>
            <div style={s.submitNote}>
              {totalSlots === 0
                ? 'No time slots added yet — go back and use "+ Add Slot" on a building first.'
                : 'Sends this schedule to TC Janitorial. You can always edit and resubmit after.'}
            </div>
            <button style={s.addBuildingBtn} onClick={() => { setSelectedBuildings(new Set()); setBuildingData({}); setView('form'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>+ Add another building →</button>
            <button style={s.cancelFormBtn} onClick={() => setView('form')}>← Back to Edit</button>
          </div>
        </div>
      )}

      {/* ── Special Events ── */}
      {view === 'events' && !showEventForm && (
        <div style={s.content}>
          <div style={s.eventsHeader}>
            <div>
              <div style={s.sectionLabel}>UPCOMING SPECIAL EVENTS</div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                Notify TC Janitorial of events requiring special setup or cleaning.
              </div>
            </div>
            <button style={s.addEventBtn} onClick={openAddEvent}>+ Add Event</button>
          </div>

          {loadingEvents && <div style={s.loadingNote}>Loading events...</div>}

          {!loadingEvents && specialEvents.length === 0 && (
            <div style={s.eventsEmpty}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📅</div>
              <div style={{ fontWeight: 700, color: '#94a3b8', marginBottom: 8 }}>No upcoming special events</div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                Use this tab to flag events that need extra setup, cleanup, or staffing — tournaments, banquets, community events, etc.
              </div>
            </div>
          )}

          {specialEvents.map(event => {
            const b = RENTAL_BUILDINGS.find(x => x.id === event.building_id);
            const det = event.details || {};
            const hasWorkers = (event.assigned_worker_names || []).length > 0;
            const isMultiDay = event.end_date && event.end_date !== event.start_date;

            return (
              <div key={event.id} style={s.eventCard}>
                <div style={s.eventCardHeader}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.eventName}>{event.name}</div>
                    <div style={s.eventLocation}>
                      {b ? `${b.id} · ${b.name}` : event.location}
                    </div>
                    <div style={s.eventDate}>
                      {isMultiDay
                        ? `${formatDateShort(event.start_date)} – ${formatDateShort(event.end_date)}`
                        : formatDate(event.start_date)}
                      {det.time_start && (
                        <span style={{ marginLeft: 6, color: '#64748b' }}>
                          · {formatTime(det.time_start)}{det.time_end ? `–${formatTime(det.time_end)}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button style={s.editEventBtn} onClick={() => openEditEvent(event)}>Edit</button>
                    <button style={s.deleteEventBtn} onClick={() => handleDeleteEvent(event.id)}>✕</button>
                  </div>
                </div>

                {det.description && <div style={s.eventDescription}>{det.description}</div>}

                {(det.estimated_attendance || det.setup_requirements || det.cleanup_requirements) && (
                  <div style={s.eventDetails}>
                    {det.estimated_attendance && (
                      <div style={s.detailRow}>
                        <span style={s.detailLabel}>Attendance</span>
                        <span>~{det.estimated_attendance} people</span>
                      </div>
                    )}
                    {det.setup_requirements && (
                      <div style={s.detailRow}>
                        <span style={s.detailLabel}>Setup</span>
                        <span>{det.setup_requirements}</span>
                      </div>
                    )}
                    {det.cleanup_requirements && (
                      <div style={s.detailRow}>
                        <span style={s.detailLabel}>Cleanup</span>
                        <span>{det.cleanup_requirements}</span>
                      </div>
                    )}
                  </div>
                )}

                {event.notes && <div style={s.eventNotes}>{event.notes}</div>}

                <div style={s.eventFooter}>
                  {hasWorkers
                    ? <span style={s.assignedPill}>Staff assigned: {event.assigned_worker_names.join(', ')}</span>
                    : <span style={s.pendingPill}>Pending staff assignment by TC Janitorial</span>
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Special Event Inline Form ── */}
      {view === 'events' && showEventForm && (
        <div style={s.content}>
          <div style={s.formPageHeader}>
            <button style={s.backBtn} onClick={() => setShowEventForm(false)}>← Back</button>
            <div style={s.formPageTitle}>{editingEvent ? `Edit: ${editingEvent.name}` : 'New Special Event'}</div>
          </div>

          <div style={s.formCard}>
            <div style={s.fieldLabel}>EVENT NAME *</div>
            <input
              style={s.formInput}
              placeholder="e.g. Easter Brunch, Youth Hockey Tournament"
              value={eventForm.name}
              onChange={e => setEventForm(p => ({ ...p, name: e.target.value }))}
            />

            <div style={s.fieldLabel}>BUILDING / FACILITY</div>
            <select
              style={s.formInput}
              value={eventForm.building_id}
              onChange={e => {
                const bid = e.target.value;
                const b = RENTAL_BUILDINGS.find(x => x.id === bid);
                setEventForm(p => ({ ...p, building_id: bid, location: b ? b.name : p.location }));
              }}
            >
              <option value="">— Select building —</option>
              {RENTAL_BUILDINGS.map(b => (
                <option key={b.id} value={b.id}>{b.id} · {b.name}</option>
              ))}
            </select>

            <div style={s.fieldLabel}>DESCRIPTION</div>
            <textarea
              style={s.formTextarea}
              rows={3}
              placeholder="What kind of event? Any context that helps cleaning staff prepare."
              value={eventForm.details.description}
              onChange={e => updateDetail('description', e.target.value)}
            />

            <div style={s.twoCol}>
              <div style={{ flex: 1 }}>
                <div style={s.fieldLabel}>EVENT DATE *</div>
                <input
                  style={s.formInput}
                  type="date"
                  value={eventForm.start_date}
                  onChange={e => setEventForm(p => ({ ...p, start_date: e.target.value }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={s.fieldLabel}>END DATE (multi-day only)</div>
                <input
                  style={s.formInput}
                  type="date"
                  value={eventForm.end_date}
                  onChange={e => setEventForm(p => ({ ...p, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div style={s.fieldLabel}>EVENT TIME</div>
            <div style={s.timeRow}>
              <select
                style={{ ...s.formInput, flex: 1, marginBottom: 0 }}
                value={eventForm.details.time_start}
                onChange={e => updateDetail('time_start', e.target.value)}
              >
                <option value="">Start time</option>
                {TIME_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <span style={s.timeSep}>→</span>
              <select
                style={{ ...s.formInput, flex: 1, marginBottom: 0 }}
                value={eventForm.details.time_end}
                onChange={e => updateDetail('time_end', e.target.value)}
              >
                <option value="">End time</option>
                {TIME_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>

            <div style={s.fieldLabel}>ESTIMATED ATTENDANCE</div>
            <input
              style={s.formInput}
              type="number"
              min="0"
              placeholder="e.g. 150"
              value={eventForm.details.estimated_attendance}
              onChange={e => updateDetail('estimated_attendance', e.target.value)}
            />

            <div style={s.fieldLabel}>SETUP REQUIREMENTS</div>
            <textarea
              style={s.formTextarea}
              rows={3}
              placeholder="e.g. Tables and chairs set up by 10am, floors polished night before"
              value={eventForm.details.setup_requirements}
              onChange={e => updateDetail('setup_requirements', e.target.value)}
            />

            <div style={s.fieldLabel}>CLEANUP REQUIREMENTS</div>
            <textarea
              style={s.formTextarea}
              rows={3}
              placeholder="e.g. Full washroom clean, floor mop, garbage removal after event"
              value={eventForm.details.cleanup_requirements}
              onChange={e => updateDetail('cleanup_requirements', e.target.value)}
            />

            <div style={s.fieldLabel}>SPECIAL NOTES / INSTRUCTIONS</div>
            <textarea
              style={s.formTextarea}
              rows={3}
              placeholder="Access codes, contact person, timing constraints, etc."
              value={eventForm.notes}
              onChange={e => setEventForm(p => ({ ...p, notes: e.target.value }))}
            />
          </div>

          <div style={s.submitArea}>
            <button
              style={(!eventForm.name || !eventForm.start_date || savingEvent)
                ? { ...s.submitBtn, opacity: 0.5 }
                : s.submitBtn}
              disabled={!eventForm.name || !eventForm.start_date || savingEvent}
              onClick={handleSaveEvent}
            >
              {savingEvent ? 'Saving...' : editingEvent ? 'Save Changes' : 'Submit to TC Janitorial'}
            </button>
            <button style={s.cancelFormBtn} onClick={() => setShowEventForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── History ── */}
      {view === 'history' && (
        <div style={s.content}>
        <div style={s.historyList}>
          <div style={{ padding: '12px 16px 4px', fontSize: 12, color: '#94a3b8' }}>
            Showing up to 12 weeks of past schedules.
          </div>
          {history.length === 0 && recentlyDeleted.length === 0 && <div style={s.empty}>No submissions yet.</div>}
          {Object.entries(
            history.reduce((acc, r) => { (acc[r.week_start] = acc[r.week_start] || []).push(r); return acc; }, {})
          ).map(([week, records]) => (
            <div key={week} style={s.historyWeek}>
              <div style={s.historyWeekHeader}>
                <div style={s.historyWeekLabel}>{formatWeekShort(week)}</div>
                <button style={s.deleteWeekBtn} onClick={() => handleDeleteWeekSchedules(week)}>Delete Week</button>
              </div>
              {records.map(r => {
                const b = RENTAL_BUILDINGS.find(x => x.id === r.building_id);
                return (
                  <div key={r.id} style={s.historyCard}>
                    <div style={s.historyBuildingName}>
                      <span style={s.historyBId}>{r.building_id}</span>
                      <span style={{ flex: 1 }}>{b?.short || r.building_id}</span>
                      <button style={s.deleteRecordBtn} onClick={() => handleDeleteSchedule(r)}>✕ Remove</button>
                    </div>
                    {b?.address && <div style={{ fontSize: 11, color: '#475569', marginBottom: 6 }}>📍 {b.address}</div>}
                    <div style={s.historyWindows}>
                      {(r.windows || []).map((w, i) => {
                        const t = w.type || 'time';
                        const typeLabel = t === 'time' ? 'Specific Time' : t === 'anytime' ? 'Anytime' : 'TC to Schedule';
                        const timeLabel = t === 'time'
                          ? `${getDayLabel(r.week_start, w.day, true)} ${w.end ? `${formatTime(w.start || '13:00')}–${formatTime(w.end)}` : `after ${formatTime(w.start || '13:00')}`}`
                          : `${getDayLabel(r.week_start, w.day, true)} · ${typeLabel}`;
                        return (
                          <span key={i} style={s.windowChip}>{timeLabel}</span>
                        );
                      })}
                      {(r.windows || []).length === 0 && (
                        <span style={{ color: '#475569', fontSize: 12 }}>No time slots specified</span>
                      )}
                    </div>
                    <div style={s.historyMeta}>
                      Min. {r.min_staff} staff
                      {r.assigned_worker_names?.length > 0 && (
                        <span style={{ color: '#22c55e' }}> · Assigned: {r.assigned_worker_names.join(', ')}</span>
                      )}
                    </div>
                    {r.restrictions && <div style={s.historyNote}>⚠️ {r.restrictions}</div>}
                  </div>
                );
              })}
            </div>
          ))}

          {/* ── Recently Deleted ── */}
          {recentlyDeleted.length > 0 && (
            <div style={s.recentlyDeletedSection}>
              <div style={s.recentlyDeletedHeader}>Recently Deleted</div>
              <div style={{ fontSize: 11, color: '#475569', marginBottom: 10 }}>
                Entries deleted in the last 7 days. Restore to return them to history.
              </div>
              {recentlyDeleted.map(item => {
                const b = RENTAL_BUILDINGS.find(x => x.id === item.building_id);
                return (
                  <div key={`${item.building_id}_${item.week_start}`} style={s.deletedCard}>
                    <div style={s.deletedCardInfo}>
                      <span style={s.historyBId}>{item.building_id}</span>
                      <span style={{ flex: 1, color: '#94a3b8', fontSize: 13 }}>{b?.short || item.building_id}</span>
                      <span style={s.deletedCardWeek}>{formatWeekShort(item.week_start)}</span>
                    </div>
                    <div style={s.deletedCardMeta}>
                      Deleted {formatRelativeTime(item.deletedAt)}
                      {(item.windows || []).length > 0 && ` · ${item.windows.length} slot${item.windows.length !== 1 ? 's' : ''}`}
                    </div>
                    <div style={s.deletedCardActions}>
                      <button style={s.restoreBtn} onClick={() => handleRestoreDeleted(item)}>Restore</button>
                      <button style={s.permDeleteBtn} onClick={() => handlePermanentDelete(item)}>Delete Forever</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
}

const s = {
  screen: { minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', paddingBottom: 40, fontFamily: "'Segoe UI', system-ui, sans-serif" },

  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '14px 20px 12px', borderBottom: '1px solid #334155',
    background: '#1e3a5f', maxWidth: 880, margin: '0 auto',
  },
  logo: { display: 'flex', alignItems: 'center', gap: 12 },
  logoMark: {
    width: 38, height: 38, borderRadius: 10,
    background: '#f97316',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 900, color: '#fff', flexShrink: 0,
  },
  logoName: { fontSize: 17, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.3px' },
  logoSub: { fontSize: 11, color: '#94a3b8', marginTop: 1 },
  signOutBtn: {
    background: 'transparent', border: '1px solid #334155', color: '#94a3b8',
    borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
  },

  tabRow: { display: 'flex', borderBottom: '1px solid #1e293b', background: '#1e293b', maxWidth: 880, margin: '0 auto' },
  tab: {
    flex: 1, background: 'transparent', border: 'none', color: '#64748b',
    padding: '13px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    borderBottom: '2px solid transparent',
  },
  tabActive: { color: '#f97316', borderBottom: '2px solid #f97316' },

  content: { padding: '0 0 20px', maxWidth: 880, margin: '0 auto' },
  section: { padding: '20px 16px 4px' },
  sectionLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: '#475569',
    textTransform: 'uppercase', marginBottom: 10,
  },

  weekSelect: {
    width: '100%', background: '#1e293b', border: '1px solid #334155',
    borderRadius: 10, padding: '13px 14px', color: '#f1f5f9', fontSize: 15,
    outline: 'none', appearance: 'auto',
  },
  loadingNote: { fontSize: 12, color: '#475569', marginTop: 8, textAlign: 'center' },

  buildingChips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
    padding: '10px 14px', cursor: 'pointer',
  },
  chipActive: { background: '#f97316', border: '1px solid #f97316' },
  chipId: { fontSize: 14, fontWeight: 800, color: '#94a3b8' },
  chipIdActive: { color: '#000' },
  chipName: { fontSize: 12, color: '#94a3b8' },
  chipNameActive: { color: '#000' },
  hintText: { fontSize: 13, color: '#64748b', marginTop: 10, textAlign: 'center', padding: '8px 0' },
  copyMsg: { fontSize: 13, color: '#4ade80', marginTop: 8, textAlign: 'center', padding: '8px 0', fontWeight: 600 },

  townLegendRow: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  legendItem: { fontSize: 12, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 6 },
  legendSwatch: { width: 12, height: 12, borderRadius: 3, flexShrink: 0, display: 'inline-block' },
  legendNote: { fontSize: 11, color: '#64748b', marginTop: 8 },

  buildingCard: {
    margin: '16px 12px 0', background: '#111827', border: '1px solid #1e293b',
    borderRadius: 14, padding: 16, borderLeft: '3px solid #f97316',
  },
  buildingCardHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 },
  collapseToggle: {
    display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0,
    background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0,
  },
  headerActions: { display: 'flex', gap: 6, flexShrink: 0 },
  collapseLabel: { fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 },
  collapsedHint: { fontSize: 11, color: '#64748b', marginTop: 2 },
  buildingCardId: {
    width: 36, height: 36, borderRadius: 9, background: '#2d1a08', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 800, color: '#f97316',
  },
  buildingCardName: { fontSize: 14, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.3 },
  copyBtn: {
    background: '#0f172a', border: '1px solid #334155', color: '#64748b',
    borderRadius: 7, padding: '6px 10px', fontSize: 11, cursor: 'pointer',
    whiteSpace: 'nowrap', flexShrink: 0,
  },

  fieldLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: '#94a3b8',
    textTransform: 'uppercase', marginBottom: 8, marginTop: 14,
  },
  emptyWindows: { fontSize: 13, color: '#475569', marginBottom: 8 },

  // Existing slot card
  slotCard: {
    background: '#0f172a', border: '1px solid #1e293b', borderRadius: 9,
    padding: '12px 12px', marginBottom: 8,
  },
  slotDayRow: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10 },
  slotDayChip: {
    background: 'transparent', border: '1px solid #1e293b', borderRadius: 5,
    padding: '4px 0', fontSize: 10, fontWeight: 700, color: '#475569',
    cursor: 'pointer', width: 30, textAlign: 'center', flexShrink: 0,
  },
  slotTypeRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 10 },
  slotTypeRowLabel: { fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', width: 42, flexShrink: 0 },
  slotDayChipActive: { background: '#3b82f6', border: '1px solid #3b82f6', color: '#fff' },
  dupBtn: {
    background: 'transparent', border: 'none', color: '#475569',
    fontSize: 15, cursor: 'pointer', padding: '2px 4px', marginLeft: 'auto',
  },
  slotTimeRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 },
  slotStaffRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
  dayGrid: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 },
  dayRow: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    background: '#0f172a', border: '1px solid #1e293b', borderRadius: 9,
    padding: '8px 10px',
  },
  dayRowLabel: { fontSize: 12, fontWeight: 700, color: '#e2e8f0', whiteSpace: 'nowrap', width: 92, flexShrink: 0 },
  dayTypeSelect: {
    flex: '0 1 150px', background: '#1e293b', border: '1px solid #334155', borderRadius: 7,
    padding: '7px 8px', color: '#f1f5f9', fontSize: 13, outline: 'none', minWidth: 0, appearance: 'auto',
  },
  dayTimeSelect: {
    flex: '0 1 110px', background: '#1e293b', border: '1px solid #334155', borderRadius: 7,
    padding: '7px 8px', color: '#f1f5f9', fontSize: 13, outline: 'none', minWidth: 0, appearance: 'auto',
  },
  dayStaffSelect: {
    flex: '0 1 110px', background: '#1e293b', border: '1px solid #334155', borderRadius: 7,
    padding: '7px 8px', color: '#f1f5f9', fontSize: 13, outline: 'none', minWidth: 0, appearance: 'auto',
  },
  timeSelect: {
    flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: 7,
    padding: '7px 8px', color: '#f1f5f9', fontSize: 13, outline: 'none',
    minWidth: 0, appearance: 'auto',
  },
  timeSep: { color: '#475569', fontSize: 13, flexShrink: 0 },
  removeBtn: {
    background: 'transparent', border: 'none', color: '#475569',
    fontSize: 14, cursor: 'pointer', padding: '4px 6px', flexShrink: 0,
  },
  multiSlotHint: {
    flexBasis: '100%', fontSize: 11, fontWeight: 700, color: '#fcd34d', marginTop: 2,
  },

  // Add slot panel
  addWindowBtn: {
    background: '#0f172a', border: '1px dashed #334155', color: '#fb923c',
    borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', width: '100%', marginTop: 4,
  },
  addPanel: {
    marginTop: 6, background: '#0a1628', border: '1px solid #334155',
    borderRadius: 10, padding: '12px 14px',
  },
  addPanelLabel: {
    fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#475569',
    textTransform: 'uppercase', marginBottom: 6, marginTop: 10,
  },
  presetRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  presetBtn: {
    background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
    borderRadius: 7, padding: '6px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },
  presetBtnActive: {
    background: '#1e3a5f', border: '1px solid #3b82f6', color: '#93c5fd',
  },
  addDayRow: { display: 'flex', gap: 4 },
  addDayChip: {
    flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
    padding: '7px 0', fontSize: 10, fontWeight: 700, color: '#475569',
    cursor: 'pointer', textAlign: 'center',
  },
  addDayChipActive: { background: '#3b82f6', border: '1px solid #3b82f6', color: '#fff' },
  addPanelActions: { display: 'flex', gap: 8, marginTop: 12 },
  addApplyBtn: {
    flex: 2, background: '#22c55e', border: 'none', color: '#052e16',
    borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  addCancelBtn: {
    flex: 1, background: 'transparent', border: '1px solid #334155', color: '#64748b',
    borderRadius: 8, padding: '11px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  // Schedule type selector in add panel
  typeRow: { display: 'flex', gap: 6, marginBottom: 4 },
  typeBtn: {
    flex: 1, background: '#1e293b', border: '1px solid #334155', color: '#64748b',
    borderRadius: 7, padding: '8px 4px', fontSize: 11, fontWeight: 700,
    cursor: 'pointer', textAlign: 'center',
  },
  typeBtnActive: { background: '#e2e8f0', border: '1px solid #e2e8f0', color: '#0f172a' },

  // Slot type badges on existing slot cards — 3 distinct colors matching the legend
  slotTypeLine: { display: 'flex', alignItems: 'center', marginBottom: 4 },
  specificTimeBadge: {
    background: '#0c2240', border: '1px solid #1e4a80', borderRadius: 6,
    padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#60a5fa',
  },
  anytimeBadge: {
    background: '#06251f', border: '1px solid #0d5c4a', borderRadius: 6,
    padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#2dd4bf',
  },
  tcBadge: {
    background: '#1a0c2e', border: '1px solid #4c1d95', borderRadius: 6,
    padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#a78bfa',
  },

  // Per-slot note input
  slotNoteInput: {
    width: '100%', background: 'transparent', border: 'none',
    borderTop: '1px solid #1e293b', color: '#64748b', fontSize: 11,
    padding: '6px 2px 2px', outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit', marginTop: 6,
  },

  staffRow: { display: 'flex', gap: 8, marginBottom: 4 },
  staffBtn: {
    flex: 1, background: '#0f172a', border: '1px solid #334155', color: '#64748b',
    borderRadius: 8, padding: '10px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  staffBtnActive: { background: '#1e3a5f', border: '1px solid #3b82f6', color: '#93c5fd' },
  textarea: {
    width: '100%', background: '#0f172a', border: '1px solid #334155',
    borderRadius: 8, padding: '10px 12px', color: '#f1f5f9', fontSize: 13,
    outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5,
  },
  // Draft restore banner (shown when unsaved draft is detected)
  draftBannerCard: {
    margin: '0 12px 4px', background: '#0c1f0f', border: '1px solid #166534',
    borderRadius: 12, padding: '14px 16px',
  },
  draftBannerText: { fontSize: 13, color: '#86efac', marginBottom: 10, lineHeight: 1.5 },
  draftBannerActions: { display: 'flex', gap: 8 },
  draftRestoreBtn: {
    flex: 2, background: '#16a34a', border: 'none', color: '#fff',
    borderRadius: 8, padding: '10px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  draftDiscardBtn: {
    flex: 1, background: 'transparent', border: '1px solid #166534', color: '#4ade80',
    borderRadius: 8, padding: '10px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },

  // Recently deleted section in History
  recentlyDeletedSection: {
    margin: '20px 12px 0', padding: '16px', background: '#111827',
    border: '1px solid #1e293b', borderRadius: 14,
  },
  recentlyDeletedHeader: {
    fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: '#475569',
    textTransform: 'uppercase', marginBottom: 6,
  },
  deletedCard: {
    background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10,
    padding: '12px 14px', marginBottom: 8,
  },
  deletedCardInfo: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  deletedCardWeek: { fontSize: 11, color: '#334155', flexShrink: 0 },
  deletedCardMeta: { fontSize: 11, color: '#334155', marginBottom: 10 },
  deletedCardActions: { display: 'flex', gap: 8 },
  restoreBtn: {
    flex: 2, background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
    borderRadius: 7, padding: '8px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  permDeleteBtn: {
    flex: 1, background: 'transparent', border: '1px solid #450a0a', color: '#ef4444',
    borderRadius: 7, padding: '8px 0', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },

  draftBanner: {
    background: '#0c1a2e', border: '1px solid #334155', borderRadius: 10,
    padding: '10px 14px', color: '#64748b', fontSize: 12, fontWeight: 600,
    marginBottom: 10, textAlign: 'center', letterSpacing: '0.3px',
  },
  reviewBtn: {
    width: '100%', background: '#22c55e', border: 'none', color: '#052e16',
    borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 800,
    cursor: 'pointer', letterSpacing: '-0.2px',
  },

  // Review page
  reviewWeekHeader: {
    margin: '16px 12px 4px', background: '#111827', border: '1px solid #1e293b',
    borderRadius: 14, padding: '14px 16px',
  },
  reviewWeekTitle: { fontSize: 17, fontWeight: 800, color: '#f1f5f9', marginBottom: 4 },
  reviewWeekSub: { fontSize: 12, color: '#475569' },
  reviewCard: {
    margin: '10px 12px 0', background: '#111827', border: '1px solid #1e293b',
    borderRadius: 14, padding: 16, borderLeft: '3px solid #f97316',
  },
  reviewCardHeader: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 },
  reviewEditBtn: {
    background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
    borderRadius: 7, padding: '5px 12px', fontSize: 11, fontWeight: 700,
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  },
  reviewNoSlots: { fontSize: 12, color: '#475569', fontStyle: 'italic', marginBottom: 8 },
  reviewSlotsList: { marginBottom: 10 },
  reviewDayRow: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '6px 0', borderBottom: '1px solid #1e293b',
  },
  reviewDayLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 700, minWidth: 88, flexShrink: 0, paddingTop: 3 },
  reviewSlotChips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  reviewSlotLine: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: '#0c1322', border: '1px solid #1e293b', borderRadius: 8,
    padding: '8px 10px', flex: '1 1 100%', minWidth: 0,
  },
  reviewSlotDay: { fontSize: 12, fontWeight: 700, color: '#e2e8f0', whiteSpace: 'nowrap', width: 92, flexShrink: 0 },
  reviewSlotType: { fontSize: 12, color: '#93c5fd', whiteSpace: 'nowrap', width: 116, flexShrink: 0 },
  reviewSlotTime: { fontSize: 12, color: '#cbd5e1', whiteSpace: 'nowrap', flex: 1 },
  reviewSlotStaff: { fontSize: 12, color: '#cbd5e1', whiteSpace: 'nowrap', width: 86, flexShrink: 0, textAlign: 'right' },
  reviewSlotRemove: {
    background: 'transparent', border: 'none', color: '#475569',
    fontSize: 13, cursor: 'pointer', padding: '2px 4px', flexShrink: 0,
  },
  reviewMeta: { fontSize: 11, color: '#475569', marginTop: 10 },
  reviewBuildingNote: {
    fontSize: 12, color: '#94a3b8', lineHeight: 1.5,
    background: '#0c1322', borderRadius: 7, padding: '8px 10px', marginTop: 8,
  },
  reviewSlotChipAnytime: { background: '#0c2240', border: '1px solid #1e4a80', color: '#60a5fa' },
  reviewSlotChipTC: { background: '#1a0c2e', border: '1px solid #4c1d95', color: '#a78bfa' },
  reviewEventsSection: { margin: '16px 12px 0' },
  reviewEventRow: {
    background: '#111827', border: '1px solid #1e293b', borderRadius: 10,
    padding: '12px 14px', marginTop: 8, borderLeft: '3px solid #7c3aed',
  },
  reviewEventName: { fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 },
  reviewEventMeta: { fontSize: 12, color: '#94a3b8', marginBottom: 4 },
  reviewEventDetail: { fontSize: 11, color: '#64748b', lineHeight: 1.5, marginTop: 2 },
  reviewNoEvents: { fontSize: 12, color: '#334155', textAlign: 'center', padding: '16px 0', fontStyle: 'italic' },

  submitArea: { padding: '20px 16px 0' },
  savedBanner: {
    background: '#052e16', border: '1px solid #15803d', borderRadius: 10,
    padding: '12px 14px', color: '#4ade80', fontSize: 14, fontWeight: 700,
    marginBottom: 10, textAlign: 'center',
  },
  unsentBanner: {
    background: '#3f2f0d', border: '1px solid #b9911c', borderRadius: 10,
    padding: '12px 14px', color: '#fcd34d', fontSize: 14, fontWeight: 700,
    marginBottom: 10, textAlign: 'center',
  },
  unsentDetail: {
    color: '#fde68a', fontSize: 12, fontWeight: 600, marginTop: 4,
  },
  editedBadge: {
    fontSize: 11, fontWeight: 700, color: '#fcd34d', marginTop: 3,
  },
  errorBanner: {
    background: '#3f0d0d', border: '1px solid #b91c1c', borderRadius: 10,
    padding: '12px 14px', color: '#fca5a5', fontSize: 13, fontWeight: 700,
    marginBottom: 10, textAlign: 'center',
  },
  warnBanner: {
    background: '#3f2f0d', border: '1px solid #b9911c', borderRadius: 10,
    padding: '12px 14px', color: '#fcd34d', fontSize: 13, fontWeight: 600,
    marginBottom: 10, textAlign: 'center', lineHeight: 1.5,
  },
  submitBtn: {
    width: '100%', background: '#22c55e', border: 'none', color: '#052e16',
    borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 800,
    cursor: 'pointer', letterSpacing: '-0.2px',
  },
  cancelFormBtn: {
    width: '100%', marginTop: 10, background: 'transparent', border: '1px solid #334155',
    color: '#64748b', borderRadius: 12, padding: '13px 0', fontSize: 14,
    fontWeight: 600, cursor: 'pointer',
  },
  addBuildingBtn: {
    width: '100%', marginTop: 10, background: '#3b82f6', border: 'none', color: '#fff',
    borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 800,
    cursor: 'pointer', letterSpacing: '-0.2px',
  },
  submitNote: { fontSize: 12, color: '#475569', textAlign: 'center', marginTop: 10, lineHeight: 1.5 },

  // Special events list
  eventsHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '20px 16px 12px',
  },
  addEventBtn: {
    background: '#f97316', border: 'none', color: '#000',
    borderRadius: 9, padding: '10px 16px', fontSize: 13, fontWeight: 800,
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  },
  eventsEmpty: {
    margin: '20px 16px', background: '#111827', border: '1px dashed #1e293b',
    borderRadius: 14, padding: '32px 24px', textAlign: 'center',
  },
  eventCard: {
    margin: '0 12px 12px', background: '#111827', border: '1px solid #1e293b',
    borderRadius: 14, padding: 16, borderLeft: '3px solid #f97316',
  },
  eventCardHeader: { display: 'flex', gap: 10, marginBottom: 10 },
  eventName: { fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 },
  eventLocation: { fontSize: 12, color: '#64748b', marginBottom: 4 },
  eventDate: { fontSize: 13, color: '#94a3b8', fontWeight: 600 },
  editEventBtn: {
    background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
    borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer',
  },
  deleteEventBtn: {
    background: 'transparent', border: 'none', color: '#475569',
    fontSize: 14, cursor: 'pointer', padding: '4px 6px',
  },
  eventDescription: {
    fontSize: 13, color: '#94a3b8', lineHeight: 1.6,
    background: '#0c1322', borderRadius: 8, padding: '10px 12px', marginBottom: 10,
  },
  eventDetails: {
    background: '#0c1322', border: '1px solid #1e293b', borderRadius: 8,
    padding: '10px 12px', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6,
  },
  detailRow: { display: 'flex', gap: 10, fontSize: 12, color: '#94a3b8', alignItems: 'flex-start' },
  detailLabel: { color: '#475569', fontWeight: 700, minWidth: 72, flexShrink: 0, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.5px', paddingTop: 1 },
  eventNotes: {
    fontSize: 12, color: '#f59e0b', lineHeight: 1.6,
    background: '#1c1208', border: '1px solid #78350f', borderRadius: 8,
    padding: '8px 12px', marginBottom: 10,
  },
  eventFooter: { marginTop: 8, paddingTop: 8, borderTop: '1px solid #1e293b' },
  assignedPill: { fontSize: 11, color: '#22c55e', fontWeight: 600 },
  pendingPill: { fontSize: 11, color: '#64748b', fontStyle: 'italic' },

  // Inline event form
  formPageHeader: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '16px 16px 8px', borderBottom: '1px solid #1e293b',
  },
  backBtn: {
    background: 'transparent', border: 'none', color: '#94a3b8',
    fontSize: 14, cursor: 'pointer', padding: '4px 0', fontWeight: 600,
  },
  formPageTitle: { fontSize: 16, fontWeight: 700, color: '#f1f5f9' },
  formCard: {
    margin: '16px 12px 0', background: '#111827', border: '1px solid #1e293b',
    borderRadius: 14, padding: 16,
  },
  formInput: {
    width: '100%', background: '#0f172a', border: '1px solid #334155',
    borderRadius: 8, padding: '12px 14px', color: '#f1f5f9', fontSize: 14,
    outline: 'none', boxSizing: 'border-box', marginBottom: 2, fontFamily: 'inherit',
  },
  formTextarea: {
    width: '100%', background: '#0f172a', border: '1px solid #334155',
    borderRadius: 8, padding: '12px 14px', color: '#f1f5f9', fontSize: 14,
    outline: 'none', resize: 'vertical', boxSizing: 'border-box',
    fontFamily: 'inherit', lineHeight: 1.5, marginBottom: 2,
  },
  twoCol: { display: 'flex', gap: 10 },
  timeRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 },

  // History
  historyList: { padding: '8px 12px' },
  historyWeek: { marginBottom: 20 },
  historyWeekHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px 6px' },
  historyWeekLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '1px', color: '#475569',
    textTransform: 'uppercase',
  },
  deleteWeekBtn: {
    background: 'transparent', border: '1px solid #450a0a', color: '#ef4444',
    borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },
  historyCard: {
    background: '#111827', border: '1px solid #1e293b', borderRadius: 10,
    padding: '12px 14px', marginBottom: 6,
  },
  historyBuildingName: {
    fontSize: 14, fontWeight: 700, color: '#f1f5f9',
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
  },
  historyBId: {
    background: '#2d1a08', color: '#fb923c', borderRadius: 5,
    padding: '2px 7px', fontSize: 12, fontWeight: 800,
  },
  deleteRecordBtn: {
    background: 'transparent', border: 'none', color: '#64748b',
    fontSize: 11, cursor: 'pointer', padding: '2px 4px', marginLeft: 'auto', flexShrink: 0,
  },
  historyWindows: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 },
  windowChip: {
    background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6,
    padding: '3px 8px', fontSize: 11, color: '#94a3b8',
  },
  historyMeta: { fontSize: 12, color: '#475569' },
  historyNote: { fontSize: 12, color: '#f59e0b', marginTop: 4 },
  empty: { padding: 32, textAlign: 'center', color: '#334155', fontSize: 14 },

  // Copy-from-previous-week
  copyWeekBtn: {
    display: 'block', width: '100%', marginTop: 10,
    background: '#0f172a', border: '1px dashed #334155', color: '#94a3b8',
    borderRadius: 9, padding: '10px 14px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', textAlign: 'left',
  },
  copyPanel: {
    margin: '0 12px 4px', background: '#111827', border: '1px solid #334155',
    borderRadius: 14, overflow: 'hidden',
  },
  copyPanelHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '14px 16px 10px', borderBottom: '1px solid #1e293b',
    background: '#0f172a',
  },
  copyPanelTitle: { fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 },
  copyPanelSub: { fontSize: 11, color: '#475569', lineHeight: 1.5 },
  copyPanelClose: {
    background: 'none', border: 'none', color: '#475569',
    fontSize: 16, cursor: 'pointer', padding: '2px 4px', flexShrink: 0,
  },
  copyEmpty: { padding: '16px', fontSize: 13, color: '#475569', textAlign: 'center' },
  copyItem: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '12px 16px', borderBottom: '1px solid #1e293b',
    cursor: 'pointer', background: 'transparent',
  },
  copyItemChecked: { background: '#0d1f0d' },
  copyCheckbox: { marginTop: 3, accentColor: '#f97316', flexShrink: 0, width: 16, height: 16 },
  copyItemBody: { flex: 1, minWidth: 0 },
  copyItemName: {
    fontSize: 14, fontWeight: 600, color: '#f1f5f9',
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
  },
  copyItemId: {
    background: '#2d1a08', color: '#f97316', borderRadius: 5,
    padding: '1px 6px', fontSize: 12, fontWeight: 800, flexShrink: 0,
  },
  copyOverwriteTag: {
    fontSize: 10, fontWeight: 700, color: '#f59e0b', background: '#1c1208',
    border: '1px solid #78350f', borderRadius: 4, padding: '1px 6px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  copyItemMeta: { fontSize: 11, color: '#475569', marginBottom: 6 },
  copyWindowList: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  copyWindowChip: {
    background: '#0f172a', border: '1px solid #1e293b', borderRadius: 5,
    padding: '2px 7px', fontSize: 11, color: '#64748b',
  },
  copyActions: {
    display: 'flex', gap: 10, padding: '14px 16px',
    borderTop: '1px solid #1e293b', background: '#0f172a',
  },
  copyApplyBtn: {
    flex: 2, background: '#22c55e', border: 'none', color: '#052e16',
    borderRadius: 9, padding: '12px 0', fontSize: 14, fontWeight: 800, cursor: 'pointer',
  },
  copyCancelBtn: {
    flex: 1, background: 'transparent', border: '1px solid #334155', color: '#64748b',
    borderRadius: 9, padding: '12px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  copiedBadge: {
    fontSize: 10, fontWeight: 700, color: '#4ade80', background: '#052e16',
    border: '1px solid #15803d', borderRadius: 5, padding: '2px 7px',
    marginTop: 3, display: 'inline-block', letterSpacing: '0.3px',
  },
};
